/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/AllLesionsSection.js
 *
 * v4 PATCH:
 *   - `targets` highlight set under RECIP10 is now lesion ∪ new-lesion
 *     (with legacy compatibility for old data). BP and Other are excluded.
 *   - Legend label updated to "Lesions" for RECIP (clearer than "PSMA(+)
 *     cohort" — actual PSMA(+)/(−) status is per-tp now, not link-level).
 *
 * G-A additions:
 *   - New props for RECIP: allLesionsTrackingType / setAllLesionsTrackingType
 *     (Lesion Type filter, default 'all'), volumeMode / setVolumeMode
 *     (shared with Finding/Analysis, default 'psma-positive' for RECIP).
 *   - In PSMA+ mode reads psmaPosVolumeByTimepoint instead of lesion.volume.
 *     Non-tumour ROIs are skipped (undefined → omitted from plot series).
 *   - RECIP: 5-filter row at xs={2.4}; non-RECIP: original 3-filter row unchanged.
 */

import React from 'react';
import PropTypes from 'prop-types';

import { Body, Grid, Select } from '@platform/ui';
import { AllLesionsLinePlot } from '../plots/LinePlot';
import module, { math } from '../../../modules/dicom-measurement/src';

const { round } = math;
const { guideline, getTimepoints } = module;
const {
  BODY_PARTS,
  TRACKING_TYPES,
  getBodyParts,
  getTrackingTypes,
  matchTrackingTypeFilter,
} = guideline;

/**
 * Which trackingTypes count as "highlight target" for the plot under the
 * active guideline.
 *
 * RECIP v4: lesion ∪ new-lesion (+ legacy equivalents for any unmigrated data).
 * BP and Other excluded.
 */
function getHighlightTrackingTypes(guidelineId) {
  if (guidelineId === 'RECIP10') {
    return [
      TRACKING_TYPES.LESION,
      TRACKING_TYPES.NEW_LESION,
      // Legacy v3 RECIP labels — back-compat with stored data:
      TRACKING_TYPES.TARGET,
      TRACKING_TYPES.PSMA_NEGATIVE,
      TRACKING_TYPES.NEW_PSMA_POSITIVE,
      TRACKING_TYPES.NEW_PSMA_NEGATIVE,
    ];
  }
  return [TRACKING_TYPES.TARGET];
}

/** Tumour types that contribute a value in PSMA+ mode. */
const PSMA_PLOT_TYPES = new Set([
  TRACKING_TYPES.LESION,
  TRACKING_TYPES.NEW_LESION,
  TRACKING_TYPES.TARGET,
  TRACKING_TYPES.PSMA_NEGATIVE,
  TRACKING_TYPES.NEW_PSMA_POSITIVE,
  TRACKING_TYPES.NEW_PSMA_NEGATIVE,
  TRACKING_TYPES.NEW,
]);

/**
 * Return the plot metric for one lesion.
 * In PSMA+ mode: reads psmaPosVolumeByTimepoint; non-tumour ROIs return
 * undefined so the caller skips them.
 */
function getMetricValue(
  lesion,
  roi,
  ctSeriesUid,
  measurementTechnique,
  isPsmaMode
) {
  if (measurementTechnique === 'BIDIMENSIONAL')
    return lesion.maxDiameterValue || 0;
  if (isPsmaMode) {
    if (!roi || !PSMA_PLOT_TYPES.has(roi.trackingType)) return undefined;
    const v = roi.psmaPosVolumeByTimepoint?.[ctSeriesUid];
    return typeof v === 'number' ? v : 0;
  }
  return lesion.volume || 0;
}

export const AllLesionsSection = ({
  imageSets,
  ROIContours,
  criteria,
  plotFeature,
  setPlotFeature,
  getFilteredROIContours,
  filteredBodyPart,
  setFilteredBodyPart,
  measurementTechnique,
  setMeasurementTechnique,
  allLesionsTrackingType = 'all',
  setAllLesionsTrackingType,
  volumeMode = 'all',
  setVolumeMode,
}) => {
  const isRecip = criteria?.guidelineId === 'RECIP10';
  const isPsmaMode =
    isRecip &&
    volumeMode === 'psma-positive' &&
    measurementTechnique === 'VOLUMETRIC';

  const techMap = { BIDIMENSIONAL: 'maxDiameterValue', VOLUMETRIC: 'volume' };
  const keyMap = {
    BIDIMENSIONAL: 'sumDiameter',
    VOLUMETRIC: isPsmaMode ? 'sumPsmaPosVolume' : 'sumVolume',
  };

  // RECIP: apply the section-local lesion type filter on top of the shared body-part filter
  const rois = isRecip
    ? getFilteredROIContours(ROIContours).filter(roi =>
        matchTrackingTypeFilter(roi, allLesionsTrackingType, 'RECIP10', -1)
      )
    : getFilteredROIContours(ROIContours);

  const timepoints = getTimepoints(imageSets, rois);

  const roiByNumber = new Map(rois.map(r => [r.ROINumber, r]));

  const names = {
    brain: 'Brain',
    liver: 'Liver',
    lung: 'Lung',
    'lymph-node': 'Lymph Node',
  };

  const data =
    plotFeature === 'byBodyPart'
      ? timepoints.map(t => {
          const obj = t.Lesions.reduce((acc, cur) => {
            const roi = roiByNumber.get(cur.ROINumber);
            const val = getMetricValue(
              cur,
              roi,
              t.SeriesInstanceUID,
              measurementTechnique,
              isPsmaMode
            );
            if (val === undefined) return acc;
            const sum = (acc[cur.bodyPart] || 0) + Number(val);
            return { ...acc, [cur.bodyPart]: round(sum, -2) };
          }, {});
          return { ...obj, name: t.SeriesDate };
        })
      : timepoints.map(t => {
          const obj = t.Lesions.reduce((acc, cur) => {
            const roi = roiByNumber.get(cur.ROINumber);
            const val = getMetricValue(
              cur,
              roi,
              t.SeriesInstanceUID,
              measurementTechnique,
              isPsmaMode
            );
            if (val === undefined) return acc;
            names[cur.ROINumber] = cur.ROIName;
            return {
              ...acc,
              [cur.ROINumber]: round(val, -2),
            };
          }, {});
          return { ...obj, name: t.SeriesDate };
        });

  const highlightTypes = getHighlightTrackingTypes(criteria?.guidelineId);
  const targets =
    plotFeature === 'byLesion'
      ? rois
          .filter(roi => highlightTypes.includes(roi.trackingType))
          .map(l => l.ROINumber)
      : [];
  const others =
    plotFeature === 'byLesion'
      ? rois
          .filter(roi => !highlightTypes.includes(roi.trackingType))
          .map(l => l.ROINumber)
      : plotFeature === 'byBodyPart'
      ? ['brain', 'liver', 'lung', 'lymph-node']
      : [];

  const brains =
    plotFeature === 'byLesionCategorized'
      ? rois
          .filter(roi => roi.bodyPart === BODY_PARTS.BRAIN)
          .map(l => l.ROINumber)
      : [];
  const lungs =
    plotFeature === 'byLesionCategorized'
      ? rois
          .filter(roi => roi.bodyPart === BODY_PARTS.LUNG)
          .map(l => l.ROINumber)
      : [];
  const livers =
    plotFeature === 'byLesionCategorized'
      ? rois
          .filter(roi => roi.bodyPart === BODY_PARTS.LIVER)
          .map(l => l.ROINumber)
      : [];
  const lymphnodes =
    plotFeature === 'byLesionCategorized'
      ? rois
          .filter(roi => roi.bodyPart === BODY_PARTS.LYMPHNODE)
          .map(l => l.ROINumber)
      : [];

  const targetLegendLabel =
    criteria?.guidelineId === 'RECIP10' ? 'Lesions' : 'Target lesions';
  const legends =
    plotFeature === 'byLesion'
      ? [
          [targetLegendLabel, 'var(--active-color)'],
          ['Others', 'var(--default-color)'],
        ]
      : [
          ['Brain', '#f6d55c'],
          ['Liver', '#ed553b'],
          ['Lung', '#3caea3'],
          ['Lymph Node', '#e4f1ee'],
        ];

  return (
    <Body>
      <AllLesionsLinePlot
        data={data}
        names={names}
        targets={targets}
        others={others}
        brains={brains}
        lungs={lungs}
        livers={livers}
        lymphnodes={lymphnodes}
        legends={legends}
        xAxisKey={'name'}
        ySumAxisKey={keyMap[measurementTechnique]}
      />
      {isRecip ? (
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Select
              value={filteredBodyPart}
              onChange={evt => setFilteredBodyPart(evt.target.value)}
            >
              {getBodyParts().map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
          <Grid item xs={3}>
            <Select
              value={allLesionsTrackingType}
              onChange={evt => setAllLesionsTrackingType?.(evt.target.value)}
            >
              {getTrackingTypes('RECIP10').map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
          <Grid item xs={2}>
            <Select
              value={plotFeature}
              onChange={evt => setPlotFeature(evt.target.value)}
            >
              {[
                ['byLesion', 'By Lesion'],
                ['byLesionCategorized', 'By Lesion(categorized)'],
                ['byBodyPart', 'By Body Part'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
          <Grid item xs={2}>
            <Select
              value={volumeMode}
              onChange={evt => setVolumeMode?.(evt.target.value)}
            >
              <option value="all">All Volume</option>
              <option value="psma-positive">PSMA+ Volume</option>
            </Select>
          </Grid>
          <Grid item xs={2}>
            <Select
              value={measurementTechnique}
              onChange={evt => setMeasurementTechnique(evt.target.value)}
            >
              {[
                ['BIDIMENSIONAL', 'Bidimensional'],
                ['VOLUMETRIC', 'Volumetric'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Select
              value={filteredBodyPart}
              onChange={evt => setFilteredBodyPart(evt.target.value)}
            >
              {getBodyParts().map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6}>
            <Select
              value={plotFeature}
              onChange={evt => setPlotFeature(evt.target.value)}
            >
              {[
                ['byLesion', 'By Lesion'],
                ['byLesionCategorized', 'By Lesion(categorized)'],
                ['byBodyPart', 'By Body Part'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
          <Grid item xs={3}>
            <Select
              value={measurementTechnique}
              onChange={evt => setMeasurementTechnique(evt.target.value)}
            >
              {[
                ['BIDIMENSIONAL', 'Bidimensional'],
                ['VOLUMETRIC', 'Volumetric'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Grid>
        </Grid>
      )}
    </Body>
  );
};
AllLesionsSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.object,
  plotFeature: PropTypes.string,
  setPlotFeature: PropTypes.func,
  getFilteredROIContours: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  measurementTechnique: PropTypes.string,
  setMeasurementTechnique: PropTypes.func,
  allLesionsTrackingType: PropTypes.string,
  setAllLesionsTrackingType: PropTypes.func,
  volumeMode: PropTypes.string,
  setVolumeMode: PropTypes.func,
};
